import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Divider,
  Button,
  Checkbox,
  Avatar,
  IconButton,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AdditionalContactField from "../../../../../../components/additional-contact/additional-contact";
import TextInput from "../../../../../../components/textinput/textinput";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { useFormik } from "formik";
import * as yup from "yup";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { websiteRegex } from "../../../../../../utils/Constant";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../../../../components/loading/loading";
import {
  getClientAllUsersRequest,
  getClientSubDivisionListRequest,
} from "../../../../../../redux/clients/slice";
import { addClientSubDivisionRequest } from "../../../../../../utils/APICalls";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";

type props = {
  showDialog: boolean;
  closeDialog: any;
  clientId: string;
  onAddDivision: any;
};

const validationSchema = yup.object({
  divisionName: yup.string().required("Division name is required"),
  website: yup.string().matches(websiteRegex, "Invalid Website"),
  useParentWebsite: yup.boolean(),
  useParentLogo: yup.boolean(),
  useParentContact: yup.boolean(),
});

export default function AddDivision({
  showDialog,
  closeDialog,
  clientId,
  onAddDivision,
}: props) {
  const [selectedFile, setSelectedFile] = useState<any>();
  const [selectedFileSrc, setSelectedFileSrc] = useState<any>();
  const [selectedManager, setSelectedManager] = useState("contact0");
  const [divisionManagers, setDivisionManagers] = useState<any[]>([
    { userId: "", isPrimary: true },
  ]);
  const [isDivisionManagerEmptyError, setIsDivisionManagerEmptyError] =
    useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const { loading, clientAllUsers } = useSelector(
    (state: any) => state?.clients
  );

  useEffect(() => {
    dispatch(getClientAllUsersRequest({ id: clientId }));
  }, [dispatch]); // eslint-disable-line

  const formik = useFormik({
    initialValues: {
      divisionName: "",
      website: "",
      logo: null,
      useParentWebsite: false,
      useParentLogo: false,
      useParentContact: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (!values?.useParentContact) {
        if (isDivisionManagerEmpty()) {
          setIsDivisionManagerEmptyError(true);
          return;
        }
        if (isDivisionManagerDuplicate()) {
          return;
        }
      }

      if (!isFileSizeValid()) {
        return;
      }

      let finalWebsite = null;
      if (values.website.length > 0) {
        finalWebsite = values.website.includes("http")
          ? values.website
          : `https://${values.website}`;
      }
      const formData = new FormData();
      formData.append("displayName", values?.divisionName || "");
      formData.append("useParentWebsite", String(values?.useParentWebsite));
      if (!values?.useParentWebsite) {
        formData.append("website", finalWebsite || "");
      }
      formData.append("useParentLogo", String(values?.useParentLogo));
      if (!values?.useParentLogo && values?.logo) {
        formData.append("logo", values.logo);
      }
      formData.append(
        "useParentPrimaryContact",
        String(values?.useParentContact)
      );
      if (!values?.useParentContact) {
        formData.append("divisionManagers", JSON.stringify(divisionManagers));
      }
      addSubDivision(formData);
    },
  });

  const isFileSizeValid = () => {
    if (selectedFile?.size > 5000000) {
      formik.setFieldError("logo", "File size exceeds 5MB limit.");
      return false;
    }
    return true;
  };

  const isDivisionManagerEmpty = () => {
    let isEmpty = false;
    divisionManagers.forEach((divisionManager) => {
      if (!divisionManager.userId) isEmpty = true;
    });
    return isEmpty;
  };

  const isDivisionManagerDuplicate = () => {
    let isDuplicate = false;
    const valueArr = divisionManagers.map((item) => item.userId);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const handleSelectedContactChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedManager(event.target.value);

    const allAccountingContacts = JSON.parse(JSON.stringify(divisionManagers));
    if (selectedIndex >= 0) {
      // if radio selected set to primary
      allAccountingContacts.forEach((accountingContact: any, index: number) => {
        if (index === selectedIndex) {
          accountingContact.isPrimary = true;
        } else {
          accountingContact.isPrimary = false;
        }
      });
    }
    setDivisionManagers(allAccountingContacts);
  };

  const onDivisionManaggerChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allDivisionManagers = JSON.parse(JSON.stringify(divisionManagers));
    allDivisionManagers[index].userId = e.target.value;
    setDivisionManagers(allDivisionManagers);
  };

  const handleAddContact = () => {
    setDivisionManagers((prev) => [...prev, { userId: "", isPrimary: false }]);
  };

  const onDivisionManagerDelete = (index: number) => {
    const allDivisionManagers = [...divisionManagers];
    allDivisionManagers.splice(index, 1);
    setDivisionManagers(allDivisionManagers);

    //if delete user is above selected user then reset user by -1
    const selectedIndex = Number(
      selectedManager.substring(selectedManager.length - 1)
    );
    if (selectedIndex > index)
      setSelectedManager(`contact${selectedIndex - 1}`);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileUploaded = event?.target?.files?.[0];

    if (fileUploaded) {
      setSelectedFile(fileUploaded);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFileSrc(reader.result);
      };
      reader.readAsDataURL(fileUploaded);
      if (fileUploaded.size > 5000000) {
        formik.setFieldError("logo", "File size exceeds 5MB limit.");
      } else {
        formik.setFieldValue("logo", fileUploaded);
      }
    } else {
      setSelectedFile(null);
    }
  };

  const handleFileDelete = () => {
    setSelectedFile(null);
    formik.setFieldValue("logo", null);
  };

  const addSubDivision = async (payload: any) => {
    setShowLoading(true);
    const response = await addClientSubDivisionRequest(clientId, payload);
    setShowLoading(false);
    if (response?.status === 201) {
      dispatch(getClientSubDivisionListRequest({ id: clientId }));
      onAddDivision(response.data);
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Add Client Division"
      showDialog={showDialog}
      closeDialog={closeDialog}
      buttonText="Add Division"
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Division Name</Typography>
          <TextInput
            className="field-input"
            id="divisionName"
            name="divisionName"
            label="Division Name"
            variant="outlined"
            value={formik.values.divisionName}
            onChange={formik.handleChange}
            error={
              formik.touched.divisionName && Boolean(formik.errors.divisionName)
            }
            helperText={
              formik.touched.divisionName && formik.errors.divisionName
            }
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Website</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  value={formik.values.useParentWebsite}
                  name="useParentWebsite"
                />
              }
              label="Same as Primary"
            />
            <TextInput
              sx={{ width: "100% !important", mt: 1 }}
              className="field-input"
              id="website"
              name="website"
              label="Website"
              variant="outlined"
              value={formik.values.website}
              onChange={formik.handleChange}
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={formik.touched.website && formik.errors.website}
              disabled={formik.values.useParentWebsite}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Logo</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  name="useParentLogo"
                  value={formik.values.useParentLogo}
                />
              }
              label="Same as Primary"
            />
            <Box
              sx={{
                height: 80,
                width: "100%",
                border: "1px dashed rgba(0, 0, 0, 0.12)",
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <UploadFileIcon
                sx={{ ml: 5 }}
                color={formik.values.useParentLogo ? "disabled" : "primary"}
              />
              <Box ml={2}>
                <Box sx={{ display: "flex" }}>
                  <Typography
                    color={formik.values.useParentLogo ? "disabled" : "primary"}
                    sx={{ textDecoration: "underline" }}
                  >
                    Click to upload
                  </Typography>
                  <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
                </Box>
                <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                  PNG or JPG (max. 5MB)
                </Typography>
              </Box>
              <input
                style={{
                  display: "block",
                  height: 80,
                  width: "100%",
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                name="logo"
                onClick={(event) => {
                  // same file selection issue
                  (event.target as HTMLInputElement).value = "";
                }}
                disabled={formik.values.useParentLogo}
              />
            </Box>
            {!formik.values.useParentLogo && selectedFile && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1.5px solid green",
                  p: 0.8,
                  borderRadius: 1,
                  backgroundColor: "#F7FAF7",
                }}
              >
                <Box display={"flex"} alignItems={"center"}>
                  {/* <AttachFileOutlinedIcon color="primary" /> */}
                  <Avatar
                    sx={{ ml: 0.5 }}
                    className="division-logo"
                    alt={selectedFile?.name}
                    variant="circular"
                    src={selectedFileSrc}
                  />
                  <Box sx={{ ml: 1.5 }}>
                    <Typography>
                      {selectedFile?.name || "No file selected"}
                    </Typography>
                    <Typography>
                      {" "}
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} kb`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <IconButton aria-label="delete" onClick={handleFileDelete}>
                    <DeleteOutlinedIcon color="primary" />
                  </IconButton>
                </Box>
              </Box>
            )}
            {Boolean(formik.errors.logo) && (
              <Box sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
                {formik.errors.logo}
              </Box>
            )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Division Manager</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControlLabel
              sx={{ mt: -1 }}
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  name="useParentContact"
                  value={formik.values.useParentContact}
                />
              }
              label="Same as Main Account Manager"
            />
            {!formik.values.useParentContact && (
              <>
                <Typography fontSize={14}>Primary</Typography>
                {divisionManagers?.map((item, index) => (
                  <AdditionalContactField
                    key={index}
                    index={index}
                    selectedValue={selectedManager}
                    onRadioChange={(e: any) =>
                      handleSelectedContactChange(e, index)
                    }
                    value={item.userId}
                    onValueChange={(e: any) =>
                      onDivisionManaggerChange(e, index)
                    }
                    onDelete={() => onDivisionManagerDelete(index)}
                    allUsers={clientAllUsers}
                    selectedUsers={divisionManagers}
                    isUserEmptyError={isDivisionManagerEmptyError}
                  />
                ))}

                {divisionManagers?.length < 4 && (
                  <Button
                    onClick={handleAddContact}
                    variant="text"
                    startIcon={<AddOutlinedIcon />}
                    sx={{ mt: 2 }}
                  >
                    Add Additional
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </form>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
      {(loading || showLoading) && <Loading />}
    </DialogContainer>
  );
}
